import { api } from '@/api';
import { HubBrandingUpdateInput, HubCreateRequest } from '@vivport/backend/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authKeys } from './useAuthQueries';

export const hubBrandingKeys = {
  branding: () => ['hub', 'branding'] as const,
};

export const useHubBranding = () => {
  return useQuery({
    queryKey: hubBrandingKeys.branding(),
    queryFn: () => api.hubs.getHubBranding(),
  });
};

export const useUpdateHubBranding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: HubBrandingUpdateInput) => api.hubs.updateHubBranding(input),
    onSuccess: (result) => {
      if (result.status === 'PENDING_APPROVAL') {
        toast.info('Branding update queued for approval in Mission Control');
        return;
      }
      if (result.status === 'EXECUTED') {
        queryClient.invalidateQueries({ queryKey: hubBrandingKeys.branding() });
        toast.success('Hub branding updated');
        return;
      }
      toast.error(result.error || 'Failed to update hub branding');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update hub branding');
    },
  });
};

/**
 * Create Corporate Hub mutation
 * Invalidates the user query so the new member appears in the hub switcher
 */
export const useCreateHub = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: HubCreateRequest) => api.hubs.createHub(input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      toast.success(`Hub "${result.hub.name}" created`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create hub');
    },
  });
};
